import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { loadThemeModule } from './registry'
import { UI_THEME_DEFAULT, normalizeTheme } from './types'

/**
 * Active UI theme (layout registry). Distinct from `useThemeStore`,
 * which only manages the light/dark color scheme.
 */
export const useUiThemeStore = defineStore('uiTheme', () => {
  /** @type {import('vue').Ref<import('./types').UiThemeId>} */
  const id = ref(UI_THEME_DEFAULT)
  /** @type {import('vue').Ref<import('./registry').UiThemeModule | null>} */
  const module = ref(null)
  const ready = ref(false)

  const layout = computed(() => module.value?.Layout ?? null)
  const views = computed(() => module.value?.views ?? {})

  /**
   * @param {import('./types').UiThemeId} themeId
   * @param {import('./registry').UiThemeModule | null} loaded
   */
  function applyTheme(themeId, loaded) {
    id.value = themeId
    module.value = loaded
    document.documentElement.dataset.uiTheme = themeId
  }

  /**
   * Load and activate a theme. Falls back to `default` if the module fails.
   *
   * @param {unknown} rawTheme
   */
  async function activate(rawTheme) {
    const resolved = normalizeTheme(rawTheme)

    try {
      applyTheme(resolved, await loadThemeModule(resolved))
    } catch {
      console.warn('[ui-theme] gagal memuat theme, menggunakan default')
      try {
        applyTheme(UI_THEME_DEFAULT, await loadThemeModule(UI_THEME_DEFAULT))
      } catch {
        console.warn('[ui-theme] gagal memuat theme default')
        applyTheme(UI_THEME_DEFAULT, null)
      }
    }

    ready.value = true
  }

  return {
    id,
    module,
    layout,
    views,
    ready,
    activate,
  }
})

/** Convenience alias for the UI theme store. */
export function useUiTheme() {
  return useUiThemeStore()
}
